import { loadSwiper, pauseAutoplayOnFocus, prefersReducedMotion } from '@kame/lib';

class KameHeroBanner extends HTMLElement {
  async connectedCallback() {
    const reduced = prefersReducedMotion();
    if (!reduced) this.classList.add('is-armed', 'is-first');
    requestAnimationFrame(() => this.classList.add('is-lit'));
    const lightOn = [...this.querySelectorAll('.kame-hb__glow, .kame-hb__media--lit')].flatMap((el) => el.getAnimations());
    const litImage = this.querySelector('.kame-hb__media--lit img');
    const imageReady =
      litImage && !litImage.complete
        ? new Promise((resolve) => {
            litImage.addEventListener('load', resolve, { once: true });
            litImage.addEventListener('error', resolve, { once: true });
          })
        : Promise.resolve();
    imageReady
      .then(() => Promise.allSettled(lightOn.map((animation) => animation.finished)))
      .then(() => this.classList.add('is-lit-done'));

    const container = this.querySelector('.swiper');
    if (!container || this.querySelectorAll('.swiper-slide').length < 2) return;
    const Swiper = await loadSwiper(this.dataset.swiperSrc);
    if (!this.isConnected || this.swiper) return;

    const progress = this.querySelector('[data-hero-progress]');
    const autoplay = this.dataset.autoplay === 'true' && !reduced;

    this.swiper = new Swiper(container, {
      effect: 'fade',
      fadeEffect: { crossFade: true },
      loop: true,
      speed: reduced ? 0 : 600,
      autoplay: autoplay
        ? { delay: Number(this.dataset.interval) || 6000, pauseOnMouseEnter: false, disableOnInteraction: false }
        : false,
      on: {
        init: (swiper) => this.restartProgress(swiper),
        autoplayPause: () => progress?.classList.add('is-paused'),
        autoplayResume: () => progress?.classList.remove('is-paused'),
        slideChange: (swiper) => {
          if (swiper.realIndex !== 0) this.classList.remove('is-first');
          this.updateInert(swiper);
          this.restartProgress(swiper);
        },
      },
    });

    this.updateInert(this.swiper);
    this.releaseFocus = pauseAutoplayOnFocus(this, this.swiper);
    document.addEventListener('shopify:block:select', this.onBlockSelect);
  }

  restartProgress(swiper) {
    const progress = this.querySelector('[data-hero-progress]');
    if (!progress) return;
    progress.classList.remove('is-running', 'is-paused');
    if (!swiper.autoplay?.running) return;
    void progress.offsetWidth;
    progress.classList.add('is-running');
  }

  updateInert(swiper) {
    swiper.slides.forEach((slide, index) => {
      if (index === swiper.activeIndex) slide.removeAttribute('inert');
      else slide.setAttribute('inert', '');
    });
  }

  disconnectedCallback() {
    document.removeEventListener('shopify:block:select', this.onBlockSelect);
    this.releaseFocus?.();
    this.swiper?.destroy(true, true);
    this.swiper = null;
  }

  onBlockSelect = (event) => {
    const slide = event.target.closest?.('.swiper-slide');
    if (!slide || !this.contains(slide) || !this.swiper) return;
    this.swiper.autoplay?.stop();
    this.swiper.slideToLoop(Number(slide.dataset.index));
  };
}

if (!customElements.get('kame-hero-banner')) customElements.define('kame-hero-banner', KameHeroBanner);
